/**
 * XStateStoreConnector — lightweight typed event store via @xstate/store.
 * Not mountable. Provides a shared event-driven store between UI and agents.
 *
 * Requires `@xstate/store` as an optional peer dependency.
 */

import type {
  ConnectContext,
  ConnectorContent,
  ConnectorDeclaration,
  ConnectorEntry,
  ConnectorHandle,
  ListOptions,
  OperationDescriptor,
  SearchOptions,
  SearchResult,
  ToolFace,
} from "@skaile/workspaces/connectors";
import { AbstractConnector } from "@skaile/workspaces/connectors";

interface StoreState {
  store: any; // @xstate/store Store instance
  history: Array<{ event: string; payload: unknown; at: string }>;
  onChange?: (snapshot: Record<string, unknown>) => void;
}

/**
 * Connector for `@xstate/store` — a lightweight event-driven key-value store shared between agents and UI clients.
 * Provides get, set, merge, and reset operations with an event history log. Requires the `@xstate/store` optional peer dependency.
 * @docLink packages/factory-assets/concepts#xstate-store-connector
 */
export class XStateStoreConnector extends AbstractConnector {
  readonly name = "xstate-store";

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      read: (handle, path) => this._read(handle, path),
      write: (handle, path, content) => this._write(handle, path, content),
      delete: (handle, path) => this._delete(handle, path),
      list: (handle, path, options) => this._list(handle, path, options),
      search: (handle, query, options) => this._search(handle, query, options),
      describeOperations: (handle) => this._describeOperations(handle),
      executeOp: (handle, operation, args) => this._executeOp(handle, operation, args),
    };
  }

  async connect(declaration: ConnectorDeclaration, _ctx: ConnectContext): Promise<ConnectorHandle> {
    this.log.info("connect", { id: declaration.id });
    const { createStore } = (await import("@xstate/store")) as any;

    const initial = (declaration.options?.initial as Record<string, unknown>) ?? {};

    // Create a dynamic store that accepts get/set/merge/delete events
    const store = createStore({
      context: { ...initial },
      on: {
        set: (ctx: Record<string, unknown>, event: { key: string; value: unknown }) => ({
          ...ctx,
          [event.key]: event.value,
        }),
        merge: (ctx: Record<string, unknown>, event: { data: Record<string, unknown> }) => ({
          ...ctx,
          ...event.data,
        }),
        delete: (ctx: Record<string, unknown>, event: { key: string }) => {
          const next = { ...ctx };
          delete next[event.key];
          return next;
        },
        reset: (_ctx: Record<string, unknown>, event: { initial?: Record<string, unknown> }) => ({
          ...(event.initial ?? initial),
        }),
      },
    });

    const state: StoreState = { store, history: [] };

    // Wire up subscription for change notifications
    store.subscribe((snapshot: any) => {
      state.onChange?.(snapshot.context);
    });

    return this.makeHandle(declaration, state);
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("disconnect");
    handle.status = "disconnected";
  }

  private storeState(handle: ConnectorHandle): StoreState {
    return handle.state as StoreState;
  }

  private context(handle: ConnectorHandle): Record<string, unknown> {
    return this.storeState(handle).store.getSnapshot().context;
  }

  /**
   * Register a callback fired on every state change.
   */
  onStoreChange(handle: ConnectorHandle, cb: (snapshot: Record<string, unknown>) => void): void {
    (handle.state as StoreState).onChange = cb;
  }

  // ── Tool face methods (private, wired in constructor) ─────────────────────

  private async _read(handle: ConnectorHandle, path: string): Promise<ConnectorContent | null> {
    const ctx = this.context(handle);
    if (path === "*") {
      if (Object.keys(ctx).length === 0) return null;
      return { data: JSON.stringify(ctx, null, 2), contentType: "application/json" };
    }
    const value = ctx[path];
    if (value === undefined) return null;
    return { data: JSON.stringify(value), contentType: "application/json" };
  }

  private async _write(
    handle: ConnectorHandle,
    path: string,
    content: ConnectorContent,
  ): Promise<void> {
    const data = typeof content.data === "string" ? content.data : content.data.toString("utf-8");
    let value: unknown;
    try {
      value = JSON.parse(data);
    } catch {
      value = data;
    }
    this.storeState(handle).store.send({ type: "set", key: path, value });
    this._recordHistory(handle, "set", { key: path, value });
  }

  private async _delete(handle: ConnectorHandle, path: string): Promise<boolean> {
    const ctx = this.context(handle);
    if (!(path in ctx)) return false;
    this.storeState(handle).store.send({ type: "delete", key: path });
    this._recordHistory(handle, "delete", { key: path });
    return true;
  }

  private async _list(
    handle: ConnectorHandle,
    _path?: string,
    options?: ListOptions,
  ): Promise<ConnectorEntry[]> {
    const ctx = this.context(handle);
    let keys = Object.keys(ctx);
    if (options?.glob) {
      // Escape regex metacharacters before substituting the `*` wildcard so a
      // glob like "a.b*" matches literally rather than as a regex.
      const escaped = options.glob.replace(/[.+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`^${escaped.replace(/\*/g, ".*")}$`);
      keys = keys.filter((k) => regex.test(k));
    }
    if (options?.limit) keys = keys.slice(0, options.limit);
    return keys.map((k) => ({ name: k, path: k, type: "key" as const }));
  }

  private async _search(
    handle: ConnectorHandle,
    query: string,
    options?: SearchOptions,
  ): Promise<SearchResult[]> {
    const ctx = this.context(handle);
    const results: SearchResult[] = [];
    for (const [key, value] of Object.entries(ctx)) {
      if (options?.maxResults && results.length >= options.maxResults) break;
      const str = JSON.stringify(value);
      if (key.includes(query) || str.includes(query)) {
        results.push({ path: key, snippet: str.slice(0, 200) });
      }
    }
    return results;
  }

  private _describeOperations(handle: ConnectorHandle): OperationDescriptor[] {
    const ops: OperationDescriptor[] = [
      {
        name: "get",
        description: "Get the full store snapshot (all keys and values).",
        accessLevel: "read",
        args: [],
      },
      {
        name: "get_key",
        description: "Get a single value by key.",
        accessLevel: "read",
        args: [{ name: "key", type: "string", required: true, description: "Key name" }],
      },
      {
        name: "history",
        description: "Get the event history log.",
        accessLevel: "read",
        args: [
          {
            name: "limit",
            type: "number",
            required: false,
            description: "Max entries (default: 50)",
          },
        ],
      },
    ];

    if (handle.access === "read-write") {
      ops.push(
        {
          name: "set",
          description: "Set a key to a value.",
          accessLevel: "write",
          args: [
            { name: "key", type: "string", required: true, description: "Key name" },
            {
              name: "value",
              type: "string",
              required: true,
              description: "Value (JSON-encoded for objects)",
            },
          ],
          examples: ["set --key theme --value '\"dark\"'", "set --key count --value 42"],
        },
        {
          name: "merge",
          description: "Merge an object into the store context (shallow merge).",
          accessLevel: "write",
          args: [
            {
              name: "data",
              type: "json",
              required: true,
              description: "JSON object to merge into context",
            },
          ],
          examples: ['merge --data \'{"theme":"dark","locale":"de"}\''],
        },
        {
          name: "delete",
          description: "Delete a single key from the store context.",
          accessLevel: "write",
          args: [
            { name: "key", type: "string", required: true, description: "Key name to delete" },
          ],
          examples: ["delete --key theme"],
        },
        {
          name: "reset",
          description: "Reset the store to its initial context.",
          accessLevel: "write",
          args: [],
        },
      );
    }

    return ops;
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    switch (operation) {
      case "get":
        return this._opGet(handle);
      case "get_key":
        return this._opGetKey(handle, args);
      case "history":
        return this._opHistory(handle, args);
      case "set":
        return this._opSet(handle, args);
      case "merge":
        return this._opMerge(handle, args);
      case "delete":
        return this._opDelete(handle, args);
      case "reset":
        return this._opReset(handle);
      default:
        throw new Error(`XStateStoreConnector has no operation: ${operation}`);
    }
  }

  private _opGet(handle: ConnectorHandle): string {
    return JSON.stringify(this.context(handle), null, 2);
  }

  private _opGetKey(handle: ConnectorHandle, args: Record<string, unknown>): string {
    const key = String(args.key ?? "");
    const value = this.context(handle)[key];
    return value !== undefined ? JSON.stringify(value) : "";
  }

  private _opHistory(handle: ConnectorHandle, args: Record<string, unknown>): string {
    const s = this.storeState(handle);
    const limit = Number(args.limit ?? 50);
    return JSON.stringify(s.history.slice(-limit), null, 2);
  }

  private _opSet(handle: ConnectorHandle, args: Record<string, unknown>): string {
    const s = this.storeState(handle);
    const key = String(args.key ?? "");
    let value: unknown = args.value;
    if (typeof value === "string") {
      try {
        value = JSON.parse(value);
      } catch {
        /* keep as string */
      }
    }
    const hadKey = key in this.context(handle);
    s.store.send({ type: "set", key, value });
    this._recordHistory(handle, "set", { key, value });
    this.emitChange?.({ path: key, action: hadKey ? "edit" : "create", source: "operation" });
    return "OK";
  }

  private _opMerge(handle: ConnectorHandle, args: Record<string, unknown>): string {
    const s = this.storeState(handle);
    let data = args.data;
    if (typeof data === "string") data = JSON.parse(data);
    s.store.send({ type: "merge", data });
    this._recordHistory(handle, "merge", { data });
    if (typeof data === "object" && data !== null) {
      const ctx = this.context(handle);
      for (const key of Object.keys(data as Record<string, unknown>)) {
        this.emitChange?.({
          path: key,
          action: key in ctx ? "edit" : "create",
          source: "operation",
        });
      }
    }
    return "OK";
  }

  private _opDelete(handle: ConnectorHandle, args: Record<string, unknown>): string {
    const s = this.storeState(handle);
    // Mirrors the tool-face `_delete`: skip absent keys (no phantom history
    // or broadcast), else mutate the store, record, and emit the change.
    const key = String(args.key ?? "");
    if (!(key in this.context(handle))) return "OK";
    s.store.send({ type: "delete", key });
    this._recordHistory(handle, "delete", { key });
    this.emitChange?.({ path: key, action: "delete", source: "operation" });
    return "OK";
  }

  private _opReset(handle: ConnectorHandle): string {
    const s = this.storeState(handle);
    s.store.send({ type: "reset" });
    s.history = [];
    this.emitChange?.({ path: "", action: "edit", source: "operation" });
    return "OK";
  }

  private _recordHistory(handle: ConnectorHandle, event: string, payload: unknown): void {
    const s = this.storeState(handle);
    s.history.push({ event, payload, at: new Date().toISOString() });
    if (s.history.length > 500) s.history = s.history.slice(-500);
  }
}

/**
 * Creates a new XStateStoreConnector instance.
 * @returns Configured XStateStoreConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#xstate-store-connector-factory
 */
export function createConnector(): XStateStoreConnector {
  return new XStateStoreConnector();
}
